import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { ReportTypes, SectionType, StudentSection } from 'libs/domain';
import { BehaviorSubject, combineLatest, from, Observable, ReplaySubject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { AppConfigService } from '@app/src/app/minimal/services/AppConfig';
import { AuthInfoService } from '@app/src/app/minimal/services/AuthInfo';
import { BellSchedulePermissionsService } from '@app/src/app/modules/minga-manager/components/mm-bell-schedule/services/bell-schedule-permissions.service';
import { PermissionsService } from '@app/src/app/permissions';
import { HallPassService } from '@app/src/app/services/HallPass';
import { OptionItem } from '@app/src/app/shared/components/form/components/form-grouped-select/form-grouped-select.types';
import { SystemAlertSnackBarService } from '@app/src/app/shared/components/system-alert-snackbar';
import { UserListCategory, UserListFilterService } from '@app/src/app/shared/components/user-list-filter';
import { QueryParamService } from '@app/src/app/shared/services/filtering/query-param.service';
import { FlexTimePeriodService, FlexTimePermissionsService } from '@app/src/app/shared/services/flex-time';
import { StudentScheduleService } from '@app/src/app/shared/services/student-schedule';
import { getToolsScheduleRoute } from '@app/src/app/shared/services/student-schedule/utils/student-schedule.utils';
import { MingaSettingsService } from '@app/src/app/store/Minga/services';

import { BehaviorManagerRoutes } from '@modules/behavior-manager';
import { CheckinManagerRoutes } from '@modules/checkin-manager';
import { HpmRoutes } from '@modules/hallpass-manager/hpm.constants';
import { MingaManagerRoutes } from '@modules/minga-manager';
import { PeopleViewProfilePerson } from '@modules/people/components/people-view-profile/types';
import { PointsManagerRoutes } from '@modules/points-manager';
import { ToolsActionsService } from '@modules/tools/services/tools.actions.service';
import { countActiveStatViews } from '@modules/tools/shared-tools/components/tools-stats-placeholder/tools-stats-placeholder.component';
import { ACTION_CATEGORY } from '@modules/tools/shared-tools/types/shared-tools.types';

import { FormSelectOption } from '@shared/components/form';
import { StatsService } from '@shared/services/stats/stats.service';
import { MingaStats } from '@shared/services/stats/stats.types';
import { defaultFilteringNavigationBehaviors, getQueryParamsFromDateRange, QueryParamKey } from '@shared/utils';

import { StudentDashboardAbstract } from '../../../shared-tools/utils/student-dashboard-abstract.utils';
import { StudentToolsFlexTimeService } from '../../../student-tools/services';
import { MyClassQueryParams } from '../../../teacher-tools/components/tt-my-class/constants/tt-my-class.constants';
import { ToolsRoutes } from '../../../tools.types';
import { AdminToolsMessages, STAT_RANGE_PRESET, StatRangeKey, STATS_RANGE_PRESETS } from '../../admin-tools.constant';

@Component({
  selector: 'mg-at-my-tools',
  templateUrl: './at-my-tools.component.html',
  styleUrls: ['./at-my-tools.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ToolsActionsService, QueryParamService],
})
export class AtMyToolsComponent extends StudentDashboardAbstract implements OnDestroy {
  /** Constants */
  public readonly MESSAGES = AdminToolsMessages;
  public readonly BEHAVIOR_MANAGER_ROUTES = BehaviorManagerRoutes;
  public readonly CHECKIN_MANAGER_ROUTES = CheckinManagerRoutes;
  public readonly POINTS_MANAGER_ROUTES = PointsManagerRoutes;
  public readonly MINGA_MANAGER_ROUTES = MingaManagerRoutes;
  public readonly REPORT_TYPES = ReportTypes;
  public readonly HPM_ROUTES = HpmRoutes;

  /** Observables */
  private _destroyedSubject = new ReplaySubject<void>(1);
  private _loadingStatsSubject = new BehaviorSubject<boolean>(true);
  public loadingStats$ = this._loadingStatsSubject.asObservable();

  private _mingaStatsSubject = new BehaviorSubject<MingaStats>(null);
  public mingaStats$ = this._mingaStatsSubject.asObservable();

  public dateRangeControl = new UntypedFormControl(StatRangeKey.LAST_7_DAYS);
  public dateRangeOptions: FormSelectOption<string>[] = STATS_RANGE_PRESETS;

  /** Observables */
  public readonly isHallpassEnabled$ = this.mingaSettings.isHallPassModuleEnabled();
  public readonly isCheckinEnabled$ = this.mingaSettings.isCheckinModuleEnabled();
  public readonly isBehaviorEnabled$ = this.mingaSettings.isPbisModuleEnabled();
  public isGroupsEnabled$ = this.mingaSettings.isCommunityModuleEnabled();
  public hasHomeroom$ = from(this._userListFilter.getMyHomeroomLists()).pipe(
    map(homerooms => homerooms.length > 0),
  );

  /**
   * Get the quantity of stats that will be displayed in the view. Used for determining
   * the placeholder layout. The stats in this list should match the stats found in the
   * view template.
   *
   * Ideally the individual stats cards would emit events for their loading status,
   * and the placeholders would be part of those components.
   */
  public readonly totalStatsCount$: Observable<number> = combineLatest([
    this.isHallpassEnabled$,
    this.isCheckinEnabled$,
    this.isBehaviorEnabled$,
  ]).pipe(
    map(stats => {
      // The other stats is currently only activeUserCount.
      const otherStatsCount = 1;
      const statViewCounts = {
        0: 1, // Hall Pass
        1: 1, // Checkin
        2: 2, // Behavior
      };

      return countActiveStatViews(stats, statViewCounts) + otherStatsCount;
    }),
  );

  public assignActions = this._toolsActions.actions
    .filter(a => a.usedIn.includes('admin'))
    .filter(a =>
      [ACTION_CATEGORY.HALL_PASS, ACTION_CATEGORY.CHECK_IN, ACTION_CATEGORY.ACTIVITY, ACTION_CATEGORY.PBIS].includes(
        a.category,
      ),
    );

  public hasAssignActions$: Observable<boolean> = combineLatest(
    this.assignActions.map(action => action.hasPermission$),
  ).pipe(map(permissions => permissions.some(hasPermission => hasPermission)));

  public person$: Observable<PeopleViewProfilePerson> = this.authInfoService.authPerson$.pipe(
    map(p => {
      if (!p) return null;

      return {
        hash: p.hash,
        badgeRoleName: p.badgeRoleName,
        firstName: p.firstName,
        lastName: p.lastName,
        profileImageUrl: p.avatarUrl,
        grade: p.grade,
        studentId: p.studentId,
        filename: p.filename,
      };
    }),
  );

  public favoriteActions$ = this._toolsActions.favoriteActions$;

  constructor(
    public appConfig: AppConfigService,
    private _statsService: StatsService,
    private _toolsActions: ToolsActionsService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _userListFilter: UserListFilterService,
    private _queryParamService: QueryParamService,
    // ... inject services from the abstract class ...
    public hallPassService: HallPassService,
    public studentScheduleService: StudentScheduleService,
    public studentFlexTime: StudentToolsFlexTimeService,
    public mingaSettings: MingaSettingsService,
    public snackBarService: SystemAlertSnackBarService,
    public permissions: PermissionsService,
    public flexPermissions: FlexTimePermissionsService,
    public bellSchedulePermissions: BellSchedulePermissionsService,
    public flexPeriod: FlexTimePeriodService,
    public authInfoService: AuthInfoService,
  ) {
    super(
      hallPassService,
      studentScheduleService,
      studentFlexTime,
      mingaSettings,
      snackBarService,
      appConfig,
      permissions,
      flexPermissions,
      bellSchedulePermissions,
      flexPeriod,
      authInfoService,
    );
  }

  // Lifecycle Hooks

  ngOnDestroy(): void {
    this._destroyedSubject.next();
    this._destroyedSubject.complete();
  }

  // Abstract Methods

  protected onInit(): void {
    this._route.queryParams.pipe(takeUntil(this._destroyedSubject)).subscribe(params => {
      const statRangeKey = params[QueryParamKey.STAT_RANGE_PRESET];
      if (statRangeKey) {
        this.dateRangeControl.setValue(statRangeKey, { emitEvent: false });
      }
      this._getMingaStats(this.dateRangeControl.value);
    });

    const authHash = this.authInfoService.authPersonHash;
    this._fetchStudentData(authHash, false);
  }

  // Public Methods

  public handleStatNavigate(route: string[]) {
    const preset = this._getPreset(this.dateRangeControl.value);
    const range = preset.getRange();
    const queryParams = getQueryParamsFromDateRange(range);
    this._router.navigate(route, {
      queryParams,
    });
  }

  public handleDateRangeChange(statRangeKey: StatRangeKey) {
    this._updateToolsUrl(statRangeKey);
  }

  public goToSchedule() {
    const route = getToolsScheduleRoute(SectionType.BELL);
    this._router.navigate(route);
  }

  public openMyClassWithScheduleItem(event: { isFlexActivity: boolean; schedule?: StudentSection }) {
    const userlist: OptionItem = {
      category: {
        value: event.isFlexActivity ? UserListCategory.TODAYS_FLEX_ACTIVITIES_MINE : UserListCategory.ALL,
        label: event.isFlexActivity ? UserListCategory.TODAYS_FLEX_ACTIVITIES_MINE : UserListCategory.ALL,
      },
      data: {
        id: event.schedule.id,
        title: event.schedule.name,
        currentTerm: true,
        isHomeroom: false,
        flexTimeActivity: {
          name: event.schedule.name,
          showProfilePicture: event.schedule.showProfilePicture ?? false,
        },
        checkinReasonId: event.schedule.checkinReasonId,
      },
      label: event.schedule.name,
      value: event.schedule.id,
    };

    this._queryParamService.updateQueryParams<MyClassQueryParams>(
      {
        lists: [userlist],
      },
      {},
      [ToolsRoutes.ROOT, ToolsRoutes.MY_CLASS],
    );
  }

  public async openMyClassWithHomeroom() {
    const homerooms = await this._userListFilter.getMyHomeroomLists();
    const homeroomOptionItems = homerooms.map(item => ({
      ...item,
      category: {
        value: 'homeroom',
      },
    }));
    this._queryParamService.updateQueryParams<MyClassQueryParams>(
      {
        lists: homeroomOptionItems,
      },
      {},
      [ToolsRoutes.ROOT, ToolsRoutes.MY_CLASS],
    );
  }

  private async _getMingaStats(statRangeKey: StatRangeKey) {
    try {
      this._loadingStatsSubject.next(true);

      const preset = this._getPreset(statRangeKey);
      const range = preset.getRange();

      const stats = await this._statsService.getMingaStats(range);
      this._mingaStatsSubject.next(stats);
    } catch (e) {
    } finally {
      this._loadingStatsSubject.next(false);
    }
  }

  private _getPreset(statRangeKey: StatRangeKey): STAT_RANGE_PRESET {
    return STATS_RANGE_PRESETS.find(p => p.value === statRangeKey);
  }

  private _updateToolsUrl(statRangeKey: StatRangeKey): void {
    this._router.navigate([], {
      queryParams: {
        [QueryParamKey.STAT_RANGE_PRESET]: statRangeKey,
      },
      ...defaultFilteringNavigationBehaviors,
    });
  }
}
